import styled from '@emotion/styled'
import {
  ExtranetFaqCategoryDto,
  GetExtranetFaqCategoryQueryDto,
} from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  FaqCategoryAccordion,
  FaqContentAccordion,
  MediaQuery,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacColumn,
  PharmacContainer,
  PharmacH4,
  PharmacH6,
  PharmacIcon,
  PharmacLoadingDots,
  PharmacSearch,
  PharmacText,
} from '@pharmac/ui'
import { useEffect, useState } from 'react'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { NoResultsResponse } from '../../components/responses/NoResultsResponse'
import { useFaqs } from '../../features/faq/useFaqs'

const TopBar = styled(PharmacContainer)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding-bottom: 2rem;

  ${MediaQuery.maxTablet} {
    padding-left: var(--spacing-medium);
    flex-direction: column;
  }
`

const SearchContainer = styled.div`
  display: flex;

  ${MediaQuery.maxTablet} {
    justify-content: flex-start;
  }
`

const AccordionTitleContainer = styled.div`
  overflow-wrap: break-word;
  hyphens: auto;
  width: 90%;
`

const AccordionContainer = styled.div`
  ${MediaQuery.maxTablet} {
    margin: 0 var(--spacing-small) 0;
  }
`

export const FaqsView = () => {
  const { key, t, i18n } = useLanguage()
  const [queryParams, setQueryParams] = useState<GetExtranetFaqCategoryQueryDto>({
    name: '',
  })
  const { faqCategories, isLoading } = useFaqs(queryParams)
  const [filteredCategories, setFilteredCategories] = useState<ExtranetFaqCategoryDto[]>([])

  const setSearchParam = (value: string) => {
    setQueryParams((s) => ({ ...s, name: value }))
  }

  useEffect(() => {
    if (queryParams.name) {
      const filtered = faqCategories.reduce<ExtranetFaqCategoryDto[]>((acc, curr) => {
        const { contents, ...category } = curr

        const matchesCategory = (() => {
          if (
            i18n.language === 'fi' &&
            category.name.fi.toLowerCase().includes(queryParams.name!.toLowerCase())
          )
            return true
          if (
            i18n.language === 'sv' &&
            category.name.sv.toLowerCase().includes(queryParams.name!.toLowerCase())
          )
            return true
          if (
            i18n.language === 'en' &&
            category.name.en.toLowerCase().includes(queryParams.name!.toLowerCase())
          )
            return true

          return false
        })()

        const findTopics = contents.filter((a) => {
          if (
            i18n.language === 'fi' &&
            a.subject.fi.toLowerCase().includes(queryParams.name!.toLowerCase())
          )
            return true
          if (
            i18n.language === 'en' &&
            a.subject.en.toLowerCase().includes(queryParams.name!.toLowerCase())
          )
            return true
          if (
            i18n.language === 'sv' &&
            a.subject.sv.toLowerCase().includes(queryParams.name!.toLowerCase())
          )
            return true

          return false
        })

        if (findTopics.length === 0 && !matchesCategory) return acc

        if (findTopics.length > 0)
          return acc.concat({
            ...category,
            contents: findTopics,
          })

        if (matchesCategory && findTopics.length === 0) return acc.concat(curr)

        return acc
      }, [])

      return setFilteredCategories(filtered)
    }

    return setFilteredCategories(faqCategories)
  }, [queryParams.name, faqCategories])

  const noResults = !isLoading && filteredCategories.length === 0

  return (
    <ExtranetWrapper>
      <TopBar>
        <PharmacColumn
          md={6}
          paddingLargeTop
        >
          <PharmacH4 color="purple">{t('faq.title')}</PharmacH4>
        </PharmacColumn>
        <PharmacColumn
          paddingLargeTop
          paddingSmallRight
          md={4}
        >
          {/** stupid hack to fix the stupid bug in PharmacSearch component */}
          <SearchContainer>
            <PharmacSearch
              onChange={setSearchParam}
              placeholder={t('search', { ns: 'common' })}
              searchButtonContent={
                <PharmacContainer marginMediumLeft>
                  <PharmacIcon
                    icon="search"
                    size="20"
                  />
                </PharmacContainer>
              }
            />
          </SearchContainer>
        </PharmacColumn>
      </TopBar>
      {isLoading && (
        <ExtranetContainer
          style={{ width: '100%' }}
          alignCenter
          justifyCenter
          paddingLarge
        >
          <PharmacLoadingDots />
        </ExtranetContainer>
      )}
      {noResults && <NoResultsResponse />}
      {filteredCategories.map((category) => (
        <AccordionContainer key={category.id}>
          <FaqCategoryAccordion
            key={category.id}
            contents={
              <>
                {category.contents.map((content) => (
                  <FaqContentAccordion
                    key={content.id}
                    data={content}
                    children={
                      <PharmacText style={{ fontSize: 'var(--typo-h6-size)' }}>
                        {key(content.subject)}
                      </PharmacText>
                    }
                  />
                ))}
              </>
            }
          >
            <AccordionTitleContainer>
              <PharmacH6>{key(category.name)}</PharmacH6>
            </AccordionTitleContainer>
          </FaqCategoryAccordion>
        </AccordionContainer>
      ))}
    </ExtranetWrapper>
  )
}
